import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Download, Printer } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  buildRegistrationSubmitSchema,
  formatDateTime,
  isMinor,
  type RegistrationField,
} from '@paroquia/shared'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageLoader,
  Spinner,
  Textarea,
} from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError, apiGet, apiPost } from '@/lib/api'
import type { ComprovanteResponse, EventDetail, RegistrationResult } from '@/lib/portal-types'

type Valores = Record<string, unknown>

/**
 * Formulario publico de inscricao (RF03.1).
 *
 * Os campos sao renderizados a partir da definicao que o coordenador montou, e
 * validados pelo MESMO schema Zod que o servidor usa
 * (`buildRegistrationSubmitSchema`). Por isso e impossivel o navegador aceitar
 * algo que a API recusa, ou o contrario.
 */
export function InscricaoPage() {
  const { slug } = useParams()

  const { data: evento, isLoading } = useQuery({
    queryKey: ['portal', 'evento', slug],
    queryFn: () => apiGet<EventDetail>(`/api/portal/eventos/${slug}`),
    enabled: !!slug,
  })

  useDocumentTitle(evento ? `Inscricao - ${evento.title}` : 'Inscricao')

  const [valores, setValores] = useState<Valores>({ data: {} })
  const [erros, setErros] = useState<Record<string, string[]>>({})
  const [resultado, setResultado] = useState<RegistrationResult | null>(null)

  const enviar = useMutation({
    mutationFn: async () => {
      if (!evento?.formulario) throw new Error('Formulario indisponivel')

      const schema = buildRegistrationSubmitSchema({
        fields: evento.formulario.campos,
        collectBirthDate: evento.formulario.collectBirthDate,
        requireGuardianForMinors: evento.formulario.requireGuardianForMinors,
      })

      const parsed = schema.safeParse(valores)
      if (!parsed.success) {
        const porCampo: Record<string, string[]> = {}
        for (const issue of parsed.error.issues) {
          const chave = issue.path.join('.') || '(formulario)'
          porCampo[chave] = [...(porCampo[chave] ?? []), issue.message]
        }
        throw new ApiError(400, 'VALIDATION_ERROR', 'Revise os campos destacados.', porCampo)
      }

      return apiPost<RegistrationResult>(`/api/portal/eventos/${slug}/inscricao`, parsed.data)
    },
    onSuccess: (dados) => {
      setErros({})
      setResultado(dados)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (error) => setErros(error instanceof ApiError ? (error.fields ?? {}) : {}),
  })

  if (isLoading) return <PageLoader />
  if (!evento) return null

  if (resultado) {
    return <ConfirmacaoDeInscricao evento={evento} resultado={resultado} />
  }

  if (!evento.formulario || !evento.inscricoes?.aberta) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="font-serif text-2xl font-semibold">Inscricoes indisponiveis</h1>
        <p className="mt-2 text-suave">
          {evento.inscricoes?.motivo ?? 'Este evento nao tem inscricao online.'}
        </p>
        <Link to={`/eventos/${evento.slug}`} className="mt-6 inline-block font-medium underline">
          Voltar ao evento
        </Link>
      </div>
    )
  }

  const formulario = evento.formulario
  const dados = (valores.data ?? {}) as Record<string, unknown>

  function definir(chave: string, valor: unknown) {
    setValores((atual) => ({ ...atual, [chave]: valor }))
  }
  function definirCampo(id: string, valor: unknown) {
    setValores((atual) => ({ ...atual, data: { ...((atual.data ?? {}) as object), [id]: valor } }))
  }

  const nascimento = typeof valores.birthDate === 'string' ? valores.birthDate : ''
  // Os campos de responsavel aparecem no instante em que a data digitada
  // indica menor de idade - nao depois do envio falhar.
  const exigeResponsavel =
    formulario.requireGuardianForMinors && nascimento.length === 10 && isMinor(nascimento)

  const erroGeral =
    enviar.error instanceof ApiError && !enviar.error.fields ? enviar.error.message : null

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Link to={`/eventos/${evento.slug}`} className="text-sm font-medium underline">
        Voltar ao evento
      </Link>

      <h1 className="mt-3 font-serif text-3xl font-semibold">Inscricao</h1>
      <p className="mt-1 text-lg text-suave">{evento.title}</p>
      <p className="text-sm text-suave">
        {formatDateTime(evento.startsAt)} — {evento.location}
      </p>

      <Alert tone="warning" className="mt-6">
        <strong>O pagamento e feito pessoalmente.</strong> Este formulario apenas registra sua
        inscricao. {evento.priceInfo ? `Valores: ${evento.priceInfo}.` : ''}
      </Alert>

      {evento.inscricoes.lotado && (
        <Alert tone="info" className="mt-3">
          As vagas terminaram. Sua inscricao entra na lista de espera.
        </Alert>
      )}

      <Card className="mt-6">
        <CardBody>
          <form
            noValidate
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault()
              enviar.mutate()
            }}
          >
            {erroGeral && <Alert tone="danger">{erroGeral}</Alert>}

            <Field label="Nome completo" error={erros.fullName} required>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="name"
                  value={(valores.fullName as string) ?? ''}
                  onChange={(event) => definir('fullName', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Telefone com DDD"
              error={erros.phone}
              hint="Usamos apenas para avisar sobre este evento."
              required
            >
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(18) 99999-9999"
                  value={(valores.phone as string) ?? ''}
                  onChange={(event) => definir('phone', event.target.value)}
                />
              )}
            </Field>

            <Field label="E-mail (opcional)" error={erros.email}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="email"
                  value={(valores.email as string) ?? ''}
                  onChange={(event) => definir('email', event.target.value)}
                />
              )}
            </Field>

            {formulario.collectBirthDate && (
              <Field label="Data de nascimento" error={erros.birthDate} required>
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    autoComplete="bday"
                    value={nascimento}
                    onChange={(event) => definir('birthDate', event.target.value)}
                  />
                )}
              </Field>
            )}

            {exigeResponsavel && (
              <fieldset className="rounded-md border border-aviso bg-aviso-suave p-4">
                <legend className="px-1 text-sm font-semibold text-aviso">
                  Inscrito menor de 18 anos
                </legend>
                <p className="text-sm text-aviso">
                  Para menores de idade, a paroquia precisa dos dados de um responsavel legal.
                </p>

                <div className="mt-3 space-y-4">
                  <Field label="Nome do responsavel" error={erros.guardianName} required>
                    {(props) => (
                      <Input
                        {...props}
                        value={(valores.guardianName as string) ?? ''}
                        onChange={(event) => definir('guardianName', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Telefone do responsavel" error={erros.guardianPhone} required>
                    {(props) => (
                      <Input
                        {...props}
                        type="tel"
                        inputMode="tel"
                        placeholder="(18) 99999-9999"
                        value={(valores.guardianPhone as string) ?? ''}
                        onChange={(event) => definir('guardianPhone', event.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
            )}

            {formulario.campos.map((campo) => (
              <CampoDinamico
                key={campo.id}
                campo={campo}
                valor={dados[campo.id]}
                erro={erros[`data.${campo.id}`]}
                onChange={(valor) => definirCampo(campo.id, valor)}
              />
            ))}

            <Field label="Observacoes (opcional)" error={erros.notes}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={(valores.notes as string) ?? ''}
                  onChange={(event) => definir('notes', event.target.value)}
                />
              )}
            </Field>

            {/*
             * Honeypot: invisivel para quem usa a pagina, atraente para robo.
             * aria-hidden e tabIndex -1 mantem leitor de tela e teclado longe.
             */}
            <div aria-hidden="true" className="absolute -left-[9999px]">
              <label htmlFor="site-web">Site</label>
              <input
                id="site-web"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={(valores.trap as string) ?? ''}
                onChange={(event) => definir('trap', event.target.value)}
              />
            </div>

            <div className="rounded-md border border-borda bg-afundado p-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={valores.consent === true}
                  onChange={(event) => definir('consent', event.target.checked)}
                  className="mt-1 size-5 shrink-0"
                  aria-describedby="texto-consentimento"
                />
                <span id="texto-consentimento" className="text-sm">
                  {formulario.consentText}{' '}
                  <Link to="/privacidade" className="underline">
                    Ver politica de privacidade
                  </Link>
                  .
                </span>
              </label>
              {erros.consent && (
                <p role="alert" className="mt-2 text-sm font-medium text-perigo">
                  {erros.consent.join('. ')}
                </p>
              )}
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={enviar.isPending}>
              {enviar.isPending ? <Spinner label="Enviando" /> : 'Confirmar inscricao'}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}

/** Renderiza um campo do construtor conforme o tipo definido pelo coordenador. */
function CampoDinamico({
  campo,
  valor,
  erro,
  onChange,
}: {
  campo: RegistrationField
  valor: unknown
  erro?: string[]
  onChange: (valor: unknown) => void
}) {
  const comum = { label: campo.label, error: erro, hint: campo.help, required: campo.required }

  switch (campo.type) {
    case 'TEXTAREA':
      return (
        <Field {...comum}>
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              maxLength={campo.maxLength}
              value={(valor as string) ?? ''}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
        </Field>
      )

    case 'SELECT':
      return (
        <Field {...comum}>
          {(props) => (
            <select
              {...props}
              value={(valor as string) ?? ''}
              onChange={(event) => onChange(event.target.value)}
              className="h-11 w-full rounded-md border border-borda bg-cartao px-3"
            >
              <option value="">Selecione...</option>
              {campo.options.map((opcao) => (
                <option key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </option>
              ))}
            </select>
          )}
        </Field>
      )

    case 'RADIO':
      return (
        <fieldset>
          <legend className="text-sm font-medium">
            {campo.label}
            {campo.required && <span className="ml-1 text-perigo">*</span>}
          </legend>
          {campo.help && <p className="text-sm text-suave">{campo.help}</p>}
          <div className="mt-2 space-y-2">
            {campo.options.map((opcao) => (
              <label key={opcao.value} className="flex min-h-11 items-center gap-3">
                <input
                  type="radio"
                  name={campo.id}
                  value={opcao.value}
                  checked={valor === opcao.value}
                  onChange={() => onChange(opcao.value)}
                  className="size-5"
                />
                <span>{opcao.label}</span>
              </label>
            ))}
          </div>
          {erro && (
            <p role="alert" className="mt-1 text-sm font-medium text-perigo">
              {erro.join('. ')}
            </p>
          )}
        </fieldset>
      )

    case 'CHECKBOX_GROUP': {
      const marcados = Array.isArray(valor) ? (valor as string[]) : []
      return (
        <fieldset>
          <legend className="text-sm font-medium">
            {campo.label}
            {campo.required && <span className="ml-1 text-perigo">*</span>}
          </legend>
          {campo.help && <p className="text-sm text-suave">{campo.help}</p>}
          <div className="mt-2 space-y-2">
            {campo.options.map((opcao) => (
              <label key={opcao.value} className="flex min-h-11 items-center gap-3">
                <input
                  type="checkbox"
                  checked={marcados.includes(opcao.value)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...marcados, opcao.value]
                        : marcados.filter((item) => item !== opcao.value),
                    )
                  }
                  className="size-5"
                />
                <span>{opcao.label}</span>
              </label>
            ))}
          </div>
          {erro && (
            <p role="alert" className="mt-1 text-sm font-medium text-perigo">
              {erro.join('. ')}
            </p>
          )}
        </fieldset>
      )
    }

    case 'CONSENT':
      return (
        <div>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={valor === true}
              onChange={(event) => onChange(event.target.checked)}
              className="mt-1 size-5 shrink-0"
            />
            <span className="text-sm">
              {campo.label}
              {campo.required && <span className="ml-1 text-perigo">*</span>}
            </span>
          </label>
          {erro && (
            <p role="alert" className="mt-1 text-sm font-medium text-perigo">
              {erro.join('. ')}
            </p>
          )}
        </div>
      )

    default: {
      const tipoDoInput =
        campo.type === 'EMAIL'
          ? 'email'
          : campo.type === 'PHONE'
            ? 'tel'
            : campo.type === 'NUMBER'
              ? 'number'
              : campo.type === 'DATE'
                ? 'date'
                : 'text'

      return (
        <Field {...comum}>
          {(props) => (
            <Input
              {...props}
              type={tipoDoInput}
              inputMode={campo.type === 'PHONE' ? 'tel' : undefined}
              value={(valor as string) ?? ''}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
        </Field>
      )
    }
  }
}

/** Tela de confirmacao com o codigo e o link do comprovante. */
function ConfirmacaoDeInscricao({
  evento,
  resultado,
}: {
  evento: EventDetail
  resultado: RegistrationResult
}) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="text-center">
        <CheckCircle2 className="mx-auto size-14 text-sucesso" aria-hidden="true" />
        <h1 className="mt-4 font-serif text-3xl font-semibold">
          {resultado.listaDeEspera ? 'Voce esta na lista de espera' : 'Inscricao confirmada'}
        </h1>
        <p className="mt-2 text-suave">{evento.title}</p>
      </div>

      <Card className="mt-8 border-borda-forte">
        <CardHeader className="text-center">
          <p className="text-sm text-suave">SEU CODIGO DE INSCRICAO</p>
          <p className="mt-1 font-mono text-3xl font-bold tracking-wider text-primario">
            {resultado.code}
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert tone="warning">
            <strong>O pagamento e feito pessoalmente.</strong> {resultado.avisoPagamento}
          </Alert>

          <p className="text-sm">
            Guarde este codigo. Voce pode consultar ou reimprimir o comprovante a qualquer momento
            em{' '}
            <Link to={`/comprovante/${resultado.code}`} className="font-medium underline">
              comprovante/{resultado.code}
            </Link>
            .
          </p>

          <div className="flex flex-wrap gap-2">
            <Button asChild>
              {/* Abre no visualizador do celular em vez de baixar um arquivo
                  que o fiel depois nao encontra. */}
              <a
                href={`/api/portal/eventos/comprovante/${resultado.code}/comprovante.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download aria-hidden="true" />
                Baixar comprovante em PDF
              </a>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/comprovante/${resultado.code}`}>Ver comprovante</Link>
            </Button>
          </div>
        </CardBody>
      </Card>

      <p className="mt-6 text-center">
        <Link to={`/eventos/${evento.slug}`} className="font-medium underline">
          Voltar ao evento
        </Link>
      </p>
    </div>
  )
}

/** Consulta publica do comprovante pelo codigo. */
export function ComprovantePage() {
  const { code } = useParams()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'comprovante', code],
    queryFn: () => apiGet<ComprovanteResponse>(`/api/portal/eventos/comprovante/${code}`),
    enabled: !!code,
  })

  useDocumentTitle(data ? `Comprovante ${data.code}` : 'Comprovante')

  if (isLoading) return <PageLoader />

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="font-serif text-2xl font-semibold">Comprovante nao encontrado</h1>
        <p className="mt-2 text-suave">
          Confira o codigo digitado. Ele tem o formato XXXX-XXXX.
        </p>
        <Link to="/eventos" className="mt-6 inline-block font-medium underline">
          Ver eventos
        </Link>
      </div>
    )
  }
  if (!data) return null

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Card>
        <CardHeader className="text-center">
          <h1 className="font-serif text-xl font-semibold">Comprovante de inscricao</h1>
          <p className="mt-2 font-mono text-3xl font-bold tracking-wider text-primario">
            {data.code}
          </p>
          <p className="mt-1 text-sm font-medium">{data.statusLabel}</p>
        </CardHeader>

        <CardBody className="space-y-4">
          <Alert tone={data.paymentReceived ? 'success' : 'warning'}>
            {data.paymentReceived ? (
              <strong>Pagamento registrado pela paroquia.</strong>
            ) : (
              <>
                <strong>Pagamento pendente.</strong> {data.avisoPagamento}
              </>
            )}
          </Alert>

          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-suave">Evento</dt>
              <dd className="font-medium">{data.evento.title}</dd>
            </div>
            <div>
              <dt className="text-sm text-suave">Data e local</dt>
              <dd className="font-medium">
                {formatDateTime(data.evento.startsAt)} — {data.evento.location}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-suave">Inscrito</dt>
              <dd className="font-medium">{data.fullName}</dd>
            </div>
            <div>
              <dt className="text-sm text-suave">Telefone</dt>
              <dd className="font-medium">{data.phone}</dd>
            </div>
            {data.guardianName && (
              <div>
                <dt className="text-sm text-suave">Responsavel legal</dt>
                <dd className="font-medium">{data.guardianName}</dd>
              </div>
            )}
            {data.respostas.map((resposta) => (
              <div key={resposta.label}>
                <dt className="text-sm text-suave">{resposta.label}</dt>
                <dd className="font-medium">
                  {Array.isArray(resposta.valor)
                    ? resposta.valor.join(', ')
                    : typeof resposta.valor === 'boolean'
                      ? resposta.valor
                        ? 'Sim'
                        : 'Nao'
                      : String(resposta.valor)}
                </dd>
              </div>
            ))}
          </dl>

          {data.confirmationMessage && (
            <p className="text-sm italic text-suave">{data.confirmationMessage}</p>
          )}

          <div className="nao-imprimir flex flex-wrap gap-2 border-t border-borda pt-4">
            <Button asChild>
              <a
                href={`/api/portal/eventos/comprovante/${data.code}/comprovante.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download aria-hidden="true" />
                PDF
              </a>
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer aria-hidden="true" />
              Imprimir
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
